import useClientSearch from '@hooks/query/clients/useClientSearch';
import { EditOutlined } from '@icon';
import { Button, Modal, PageHeaderWrapper, Table } from '@ui';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { IClientSearchItem } from 'types/client.types';
import AddToPatientsModal from '../components/add-to-patients-modal';
import PatientsFilter from '../components/patients-filter';
import type { TSearchValues } from '../components/patients-filter/patients-filter';
import { TableSC } from './patients.styled';
import { INITIAL_PARAMS, INITIAL_STATE, TTableProps, useTableColumns } from './patients.util';

const Patients: React.FC = () => {
  const { push } = useHistory();

  const [searchParams, setSearchParams] = React.useState(INITIAL_PARAMS);
  const { data, isLoading, refetch, isFetching } = useClientSearch(searchParams, {
    keepPreviousData: true,
  });

  const addModal = Modal.useModal<IClientSearchItem>();
  const allClientsShown = !searchParams.myClients;

  const columns = useTableColumns({
    allClientsShown,
    onPairClick: addModal.open,
  });

  const handleTableChange = React.useCallback<TTableProps['onChange']>((pagination, sorters) => {
    let sortBy: string | undefined;
    let sortDirection: 'ASC' | 'DESC' | undefined;

    if (sorters.length > 0) {
      sortBy = sorters[0].id;
      sortDirection = sorters[0].desc ? 'DESC' : 'ASC';
    }

    setSearchParams((o) => ({
      ...o,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortBy: sortBy as any,
      direction: sortDirection,
    }));
  }, []);

  const handleFilter = React.useCallback((params: TSearchValues) => {
    setSearchParams((o) => ({
      ...o,
      page: 1,
      name: params.query,
      myClients: params.clients === 'all' ? null : true,
    }));
  }, []);

  return (
    <>
      <PageHeaderWrapper
        showDivider
        extra={
          <Button
            size="medium"
            onClick={() => push('/business/profile/patients')}
            variant="secondary"
            Icon={EditOutlined}
          >
            Správa pacientov
          </Button>
        }
      >
        <Table.Header
          title={
            (
              <FormattedMessage
                id="patients_page.yours_patients"
                values={{ count: data?.pagination?.total ?? 1 }}
              />
            ) as unknown as string
          }
          extra={<PatientsFilter refetch={refetch} onParamsChange={handleFilter} />}
          style={{ flexWrap: 'wrap' }}
        />
        <TableSC
          loading={isLoading || isFetching}
          className="patient-list"
          manualSortBy
          manualPagination
          autoResetPage
          disableMultiSort
          columns={columns}
          data={data?.items || []}
          pagination={data?.pagination}
          initialState={INITIAL_STATE}
          onChange={handleTableChange}
          showEmpty
          emptyTitle="Žiadni pacienti"
        />
        {allClientsShown ? (
          <AddToPatientsModal
            afterSubmit={refetch}
            onClose={addModal.close}
            {...addModal.modalProps}
            client={addModal.data}
            mode="paired"
          />
        ) : null}
      </PageHeaderWrapper>
    </>
  );
};

export default Patients;
